/*
 * Embed styles. Everything lives inside the shadow root, so the host page's CSS cannot reach
 * these rules; the only things that cross the boundary are inherited properties (font, color,
 * …) and CSS custom properties, which we reset / consume on purpose on `.ffs`.
 *
 * Theming (set on the host element): --ffs-accent, --ffs-font, --ffs-radius.
 */
:host {
  display: block;
}

.ffs {
  all: initial;
  display: block;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  container-type: inline-size;
  font-family: var(
    --ffs-font,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Inter,
    Roboto,
    "Helvetica Neue",
    Arial,
    sans-serif
  );
  font-size: 15px;
  line-height: 1.5;
  color: #0f172a;
  text-align: left;
  -webkit-font-smoothing: antialiased;
  --r: var(--ffs-radius, 10px);
  --ring: color-mix(in srgb, var(--a) 28%, transparent);
  --tint: color-mix(in srgb, var(--a) 8%, #fff);
}

.ffs *,
.ffs *::before,
.ffs *::after {
  box-sizing: border-box;
}

.card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: calc(var(--r) + 6px);
  padding: 24px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.card.flat {
  background: transparent;
  border: 0;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
}

/* ---- layout ---- */
.as-email {
  margin: 0 0 14px;
  font-size: 14px;
  line-height: 1.45;
  color: #475569;
  overflow-wrap: anywhere;
}

.grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  margin: 0 0 14px;
}

/* First and last name side by side unless the embed is phone-narrow (container, not viewport). */
@container (min-width: 320px) {
  .grid.two {
    grid-template-columns: 1fr 1fr;
  }
}

.field {
  margin: 0;
  min-width: 0;
}

.label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin: 0 0 6px;
}

.req {
  color: #94a3b8;
  font-weight: 400;
}

.input {
  display: block;
  width: 100%;
  margin: 0;
  padding: 10px 12px;
  font: inherit;
  font-size: 15px;
  line-height: 1.4;
  color: #0f172a;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: var(--r);
  appearance: none;
  -webkit-appearance: none;
  outline: none;
  box-shadow: none;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}

.input::placeholder {
  color: #94a3b8;
}

.input:focus,
.input:focus-visible {
  border-color: var(--a);
  box-shadow: 0 0 0 3px var(--ring);
}

.input[aria-invalid="true"] {
  border-color: #dc2626;
}

.input[aria-invalid="true"]:focus {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.2);
}

select.input {
  padding-right: 36px;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m4 6 4 4 4-4'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  cursor: pointer;
}

.phone {
  display: flex;
  gap: 8px;
}

.phone .input {
  flex: 1;
  min-width: 0;
}

.phone-cc {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  flex: none;
  padding: 0 28px 0 12px;
  font-size: 15px;
  color: #0f172a;
  background: #fff
    url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m4 6 4 4 4-4'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  border: 1px solid #cbd5e1;
  border-radius: var(--r);
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}

.phone-cc:focus-within {
  border-color: var(--a);
  box-shadow: 0 0 0 3px var(--ring);
}

.phone-flag {
  font-size: 18px;
  line-height: 1;
}

.phone-dial {
  color: #475569;
  font-variant-numeric: tabular-nums;
}

/* The native country list sits invisibly over the flag: full keyboard and mobile support. */
.phone-select {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
  font-size: 16px;
  appearance: none;
  -webkit-appearance: none;
}

.err {
  display: block;
  margin: 6px 0 0;
  font-size: 13px;
  line-height: 1.4;
  color: #b91c1c;
}

.check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  color: #334155;
  cursor: pointer;
}

.check input {
  flex: none;
  width: 18px;
  height: 18px;
  margin: 2px 0 0;
  accent-color: var(--a);
  cursor: pointer;
}

.check input:focus-visible {
  outline: 3px solid var(--ring);
  outline-offset: 2px;
  border-radius: 4px;
}

/* ---- slot picker ---- */
.slots-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  margin: 0 0 8px;
}

.slots-head .label {
  margin: 0;
}

.tz {
  max-width: 100%;
  margin: 0;
  padding: 4px 26px 4px 8px;
  font: inherit;
  font-size: 13px;
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  outline: none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m3.5 5 3.5 3.5L10.5 5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
}

.tz:focus-visible {
  border-color: var(--a);
  box-shadow: 0 0 0 3px var(--ring);
}

.slots {
  display: grid;
  gap: 8px;
  margin: 0 0 14px;
}

.slot {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  padding: 11px 14px;
  font-size: 15px;
  color: #0f172a;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: var(--r);
  cursor: pointer;
  transition:
    border-color 0.15s,
    background-color 0.15s,
    box-shadow 0.15s;
}

.slot:hover {
  border-color: #94a3b8;
}

.slot input {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: 0;
  opacity: 0;
  pointer-events: none;
}

.dot {
  flex: none;
  width: 18px;
  height: 18px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
  background: #fff;
  transition: border 0.15s;
}

.slot.sel {
  border-color: var(--a);
  background: var(--tint);
  box-shadow: inset 0 0 0 1px var(--a);
}

.slot.sel .dot {
  border: 6px solid var(--a);
}

.slot:has(input:focus-visible) {
  box-shadow: 0 0 0 3px var(--ring);
}

.slot-text {
  flex: 1 1 auto;
  min-width: 0;
}

.badge {
  flex: none;
  margin-left: auto;
  padding: 2px 9px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: var(--a);
  background: color-mix(in srgb, var(--a) 12%, #fff);
  border-radius: 999px;
}

.empty {
  margin: 0 0 14px;
  padding: 18px 14px;
  font-size: 14px;
  color: #64748b;
  text-align: center;
  border: 1px dashed #cbd5e1;
  border-radius: var(--r);
}

.skel {
  height: 46px;
  border-radius: var(--r);
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: ffs-shimmer 1.2s linear infinite;
}

.skel.short {
  height: 16px;
  width: 40%;
}

@keyframes ffs-shimmer {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .skel {
    animation: none;
  }
}

/* ---- button, alerts ---- */
.btn {
  display: block;
  width: 100%;
  margin: 8px 0 0;
  padding: 13px 18px;
  font: inherit;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--btn-fg, #fff);
  text-align: center;
  text-decoration: none;
  background: var(--btn, var(--a));
  border: 0;
  border-radius: var(--r);
  cursor: pointer;
  transition:
    filter 0.15s,
    opacity 0.15s;
}

.btn:hover {
  filter: brightness(0.92);
}

.btn:focus-visible {
  outline: 3px solid var(--ring);
  outline-offset: 2px;
}

.btn:disabled {
  opacity: 0.65;
  cursor: default;
  filter: none;
}

.alert {
  margin: 14px 0 0;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 1.45;
  color: #991b1b;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: var(--r);
}

.alert:empty {
  display: none;
}

.turnstile {
  display: flex;
  justify-content: center;
}

.turnstile[data-visible] {
  margin: 14px 0 6px;
}

.fine {
  margin: 12px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #94a3b8;
  text-align: center;
}
/* The verification line: a small shield-check before the text. */
.fine-shield {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}
.fine-shield svg {
  flex: none;
}

.fine a,
.muted a,
.link {
  color: var(--a);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.link {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  cursor: pointer;
}

.link:focus-visible,
.muted a:focus-visible,
.fine a:focus-visible,
.cal a:focus-visible {
  outline: 3px solid var(--ring);
  outline-offset: 2px;
  border-radius: 4px;
}

/* ---- confirmation ---- */
.done {
  text-align: center;
}

.tick {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  margin: 0 0 14px;
  color: var(--a);
  background: color-mix(in srgb, var(--a) 12%, #fff);
  border-radius: 50%;
}

.title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
  color: #0f172a;
}

.when {
  margin: 0 0 4px;
  font-size: 16px;
  color: #334155;
}

.sub {
  margin: 0 0 18px;
  font-size: 14px;
  color: #64748b;
}

.cal {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.cal a {
  display: inline-block;
  padding: 7px 14px;
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  text-decoration: none;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  transition: background-color 0.15s;
}

.cal a:hover {
  background: #f1f5f9;
}

.muted {
  margin: 14px 0 0;
  font-size: 13px;
  color: #64748b;
}

.sr {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

.current {
  margin: 0 0 16px;
  padding: 10px 12px;
  font-size: 14px;
  color: #334155;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: var(--r);
}

.current strong {
  color: #0f172a;
}

.fs {
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
}

.slot-err {
  margin: -6px 0 14px;
}

/* ---- custom confirmation page blocks ---- */
.countdown {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 18px;
  margin: 0;
}

.countdown-part {
  min-width: 56px;
  text-align: center;
}

.countdown-part b {
  display: block;
  font-size: 34px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: var(--a);
}

.countdown-part span {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.countdown-now {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--a);
}
